import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, TrendingUp, LogOut } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

import config from "../../config/config";
import { logout } from "../../redux/slices/adminSlice";
import { useDispatch } from "react-redux";

import VisitorsCard from "../../Components/Visitors/VisitorsCard";
import VisitorDetail from "../../Components/Visitors/VisitorDetail";
import DashboardStats from "../../Components/Visitors/DashboardStats";

const tabsData = [
  { key: "keywords", label: "All Searches" },
  { key: "brands", label: "All Brands" },
  { key: "latest-keywords", label: "Latest Searches" },
  { key: "feedback", label: "Search Feedback" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* -------------------- STATE -------------------- */
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const [visitors, setVisitors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingVisitorId, setLoadingVisitorId] = useState(null);

  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [selectedVisitorJourney, setSelectedVisitorJourney] = useState(null);

  const [collapsedActions, setCollapsedActions] = useState({});
  const [expandedSessions, setExpandedSessions] = useState({});

  /* -------- Tabs -------- */
  const [activeTab, setActiveTab] = useState("keywords");
  const [tabSearchQuery, setTabSearchQuery] = useState("");

  const LIMIT = 10;

  // Keywords
  const [keywords, setKeywords] = useState([]);
  const [keywordsOffset, setKeywordsOffset] = useState(0);
  const [keywordsHasMore, setKeywordsHasMore] = useState(true);

  // Brands
  const [brands, setBrands] = useState([]);
  const [brandsOffset, setBrandsOffset] = useState(0);
  const [brandsHasMore, setBrandsHasMore] = useState(true);

  const [allSearchFeedbacks, setAllSearchFeedbacks] = useState(null);

  /* -------------------- API CALLS -------------------- */

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${config.backendUrl}/analytics/dashboard/summary`,
        {
          params: {
            startDate: appliedStartDate || undefined,
            endDate: appliedEndDate || undefined,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSummary(res.data.stats);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/analytics/visitors`, {
        params: {
          startDate: appliedStartDate || undefined,
          endDate: appliedEndDate || undefined,
        },
      });
      setVisitors(res.data.visitors);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleVisitor = async (visitorId) => {
    setLoading(true);
    setLoadingVisitorId(visitorId);
    try {
      const res = await axios.get(
        `${config.backendUrl}/analytics/visitor/${visitorId}`,
      );
      setSelectedVisitorJourney(res.data);
    } finally {
      setLoading(false);
      setLoadingVisitorId(null);
    }
  };

  const fetchKeywords = async () => {
    if (!keywordsHasMore) return;

    const res = await axios.get(`${config.backendUrl}/analytics/keywords`, {
      params: { limit: LIMIT, offset: keywordsOffset },
    });

    const { data = [], pagination } = res.data;

    setKeywords((prev) => [...prev, ...data]);
    setKeywordsOffset((prev) => prev + LIMIT);
    setKeywordsHasMore(pagination.hasMore);
  };

  const fetchBrands = async () => {
    if (!brandsHasMore) return;

    const res = await axios.get(
      `${config.backendUrl}/analytics/visited-brands`,
      { params: { limit: LIMIT, offset: brandsOffset } },
    );

    const { data = [], pagination } = res.data;

    setBrands((prev) => [...prev, ...data]);
    setBrandsOffset((prev) => prev + LIMIT);
    setBrandsHasMore(pagination.hasMore);
  };

  const fetchSearchFeedback = async () => {
    const res = await axios.get(`${config.backendUrl}/searchfeedback/getAll`);

    // const { data = [] } = res.data;
    setAllSearchFeedbacks(res.data.data);
  };

  /* -------------------- EFFECTS -------------------- */

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchVisitors();
    fetchSummary();
  }, [appliedStartDate, appliedEndDate]);

  useEffect(() => {
    if (activeTab === "keywords" && keywords.length === 0) {
      fetchKeywords();
    }

    if (activeTab === "brands" && brands.length === 0) {
      fetchBrands();
    }
    if (activeTab === "feedback") {
      fetchSearchFeedback();
    }
  }, [activeTab]);

  /* -------------------- HELPERS -------------------- */

  const isInvalidDateRange = () => {
    if (!draftStartDate && !draftEndDate) return false;
    if (!draftStartDate || !draftEndDate) return true;
    return (
      new Date(draftStartDate) > new Date(draftEndDate) ||
      new Date(draftEndDate) > new Date()
    );
  };

  const toggleSession = (id) =>
    setExpandedSessions((p) => ({ ...p, [id]: !p[id] }));

  const toggleActionCollapse = (key) =>
    setCollapsedActions((p) => ({ ...p, [key]: !p[key] }));

  const getActionKey = (sid, idx) => `${sid}-${idx}`;
  const isCollapsibleAction = () => false;

  const getActionIcon = () => <TrendingUp className="w-5 h-5" />;
  const getActionColor = () => "bg-gray-50 border-gray-200 text-gray-700";
  const getActionBadgeColor = () => "bg-gray-100 text-gray-800";
  const getActionLabel = (a) => a;

  const formatTimestamp = (t) => new Date(t).toLocaleString("en-US");
  const formatDate = (t) => new Date(t).toLocaleDateString("en-US");

  /* -------------------- INITIAL LOADING -------------------- */

  if (!summary) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  /* -------------------- SINGLE VISITOR -------------------- */

  if (selectedVisitor) {
    return (
      <>
        <Backdrop open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <VisitorDetail
          visitorId={selectedVisitor}
          journey={selectedVisitorJourney?.journey || []}
          collapsedActions={collapsedActions}
          expandedSessions={expandedSessions}
          toggleSession={toggleSession}
          toggleActionCollapse={toggleActionCollapse}
          getActionKey={getActionKey}
          isCollapsibleAction={isCollapsibleAction}
          getActionIcon={getActionIcon}
          getActionColor={getActionColor}
          getActionBadgeColor={getActionBadgeColor}
          getActionLabel={getActionLabel}
          formatTimestamp={formatTimestamp}
          formatDate={formatDate}
          onBack={() => {
            setSelectedVisitor(null);
            setSelectedVisitorJourney(null);
          }}
        />
      </>
    );
  }

  /* -------------------- DASHBOARD -------------------- */

  return (
    <>
      <Backdrop open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="min-h-screen bg-gray-100 p-6">
        {/* Header */}
        <div className="flex justify-between mb-6">
          <h1 className="text-4xl font-bold">Analytics Dashboard</h1>
          <button
            onClick={() => {
              dispatch(logout());
              localStorage.clear();
              navigate("/login");
            }}
            className="bg-red-500 text-white px-4 py-2 rounded"
          >
            <LogOut className="inline mr-2" /> Logout
          </button>
        </div>

        {/* Stats */}
        <DashboardStats summary={summary} />

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow mb-8">
          <div className="flex border-b">
            {tabsData.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex-1 py-3 font-semibold ${
                  activeTab === t.key
                    ? "border-b-2 border-indigo-600 text-indigo-600"
                    : "text-gray-600"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "keywords" ||
          activeTab === "brands" ||
          activeTab === "latest-keywords" ? (
            <div className="p-4 border-b">
              <input
                className="w-full border p-2 rounded"
                placeholder={`Search ${
                  activeTab === "brands" ? "brands" : "keywords"
                }`}
                value={tabSearchQuery}
                onChange={(e) => setTabSearchQuery(e.target.value)}
              />
            </div>
          ) : (
            ""
          )}

          {activeTab === "keywords" || activeTab === "brands" ? (
            <div className="max-h-[300px] overflow-y-auto">
              {(activeTab === "keywords" ? keywords : brands)
                .filter((i) =>
                  (i.keyword || i.brand)
                    .toLowerCase()
                    .includes(tabSearchQuery.toLowerCase()),
                )
                .map((i) => (
                  <div key={i.keyword || i.brand} className="p-4 border-b">
                    <b>{i.keyword || i.brand}</b>
                    <span className="float-right">{i.count}</span>
                  </div>
                ))}
            </div>
          ) : activeTab === "latest-keywords" ? (
            <div className="max-h-[300px] overflow-y-auto">
              {keywords
                .filter((i) =>
                  i.keyword
                    .toLowerCase()
                    .includes(tabSearchQuery.toLowerCase()),
                )
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((i) => (
                  <div key={i.keyword} className="p-4 border-b">
                    <b>{i.keyword}</b>
                    <span className="float-right">{i.count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto border rounded-lg">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-gray-100 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left border-b text-center">
                      Keyword
                    </th>
                    <th className="px-3 py-2 text-left border-b text-center">
                      Satisfied with the search results?
                    </th>
                    <th className="px-3 py-2 text-left border-b text-center">
                      Reason
                    </th>
                    <th className="px-3 py-2 text-left border-b text-center">
                      Date
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {allSearchFeedbacks && allSearchFeedbacks.length > 0 ? (
                    allSearchFeedbacks.map((i, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-3 py-2 border-b font-medium text-center">
                          {i?.analytics?.data?.data?.keyword || "-"}
                        </td>

                        <td className="px-3 py-2 border-b text-center">
                          {i?.feedback === "up" ? (
                            <span className="text-green-600 font-semibold">
                              👍 Yes
                            </span>
                          ) : (
                            <span className="text-red-500 font-semibold">
                              👎 No
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2 border-b text-gray-600 text-center">
                          {i?.reason || "-"}
                        </td>

                        <td className="px-3 py-2 border-b text-gray-500 text-center">
                          {i?.createdAt
                            ? new Date(i.createdAt).toLocaleString()
                            : "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center py-6 text-gray-500"
                      >
                        No feedback found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* LOAD MORE */}
          {(activeTab === "keywords" || activeTab === "latest-keywords") &&
            keywordsHasMore && (
              <button
                onClick={fetchKeywords}
                className="w-full py-2 text-indigo-600 font-semibold"
              >
                Load More
              </button>
            )}

          {activeTab === "brands" && brandsHasMore && (
            <button
              onClick={fetchBrands}
              className="w-full py-2 text-indigo-600 font-semibold"
            >
              Load More
            </button>
          )}
        </div>

        {/* Visitors */}
        <VisitorsCard
          visitors={visitors}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          loadingVisitorId={loadingVisitorId}
          formatDate={formatDate}
          draftStartDate={draftStartDate}
          draftEndDate={draftEndDate}
          setDraftStartDate={setDraftStartDate}
          setDraftEndDate={setDraftEndDate}
          isInvalidDateRange={isInvalidDateRange}
          onApply={() => {
            if (!isInvalidDateRange()) {
              setAppliedStartDate(draftStartDate);
              setAppliedEndDate(draftEndDate);
            }
          }}
          onReset={() => {
            setDraftStartDate("");
            setDraftEndDate("");
            setAppliedStartDate("");
            setAppliedEndDate("");
          }}
          onVisitorSelect={(id) => {
            setSelectedVisitor(id);
            fetchSingleVisitor(id);
          }}
        />
      </div>
    </>
  );
}
